/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { EX } from './exercises.js';
import { getMedal as getMedalByLevel, getNextMedal as getNextMedalByLevel, MEDALS_DEF_LOGIC } from './medalsLogic.js';
import { getLevelFromXP, getLevelProgress } from './xpSystem.js';
import { LOGO_LIGHT_DATA, MASCOT_BUST_DATA } from './assets.js';

// ─── THEMES & COLORS ──────────────────────────────────────────────────────────
const THEMES = [
  { id:"yellow",  name:"Or",        a:"#EAB308", b:"#F97316" },
  { id:"blue",    name:"Océan",     a:"#2563EB", b:"#06B6D4" },
  { id:"pink",    name:"Rose",      a:"#EC4899", b:"#F43F5E" },
  { id:"green",   name:"Forêt",     a:"#16A34A", b:"#84CC16" },
  { id:"orange",  name:"Feu",       a:"#FF6B2C", b:"#FF3B3B", premium:true },
  { id:"purple",  name:"Violet",    a:"#7C3AED", b:"#C026D3", premium:true },
  { id:"teal",    name:"Turquoise", a:"#0D9488", b:"#22D3EE", premium:true },
  { id:"red",     name:"Rubis",     a:"#DC2626", b:"#F43F5E", premium:true },
  { id:"indigo",  name:"Indigo",    a:"#4338CA", b:"#7C3AED", premium:true },
  { id:"lime",    name:"Citron",    a:"#65A30D", b:"#D9F99D", premium:true },
  { id:"cyan",    name:"Ciel",      a:"#0891B2", b:"#38BDF8", premium:true },
  { id:"amber",   name:"Ambre",     a:"#D97706", b:"#FB923C", premium:true },
];

const mkBtn = C => ({children,onClick,variant="primary",small,full,disabled,style:s={}}) => {
  const v = {
    primary:{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,color:"white",boxShadow:`0 4px 20px ${C.glow}`},
    ghost:  {background:C.faint,color:C.muted,border:`1px solid ${C.border}`},
    outline:{background:"transparent",color:C.accent,border:`1px solid ${C.accent}`},
    danger: {background:"rgba(239,68,68,0.1)",color:C.red,border:"1px solid rgba(239,68,68,0.25)"},
  };
  return <button onClick={disabled?undefined:onClick} style={{border:"none",fontWeight:800,borderRadius:12,cursor:disabled?"default":"pointer",opacity:disabled?0.35:1,fontSize:small?13:15,letterSpacing:0.4,padding:small?"8px 16px":"14px 20px",width:full?"100%":undefined,transition:"all 0.15s",...v[variant],...s}}>{children}</button>;
};

function countTrainingDays(sessions) {
  if(!sessions||sessions.length===0) return 0;
  const uniqueDays = new Set(sessions.map(s=>s.date?s.date.slice(0,10):null).filter(Boolean));
  return uniqueDays.size;
}

function ProfilePage({profile,onSave,voiceEnabled,onToggleVoice,themeId,onSetTheme,darkMode,onToggleDark,onReset,user,onSignOut,onLogin,sessions,prs,programs,defaultProgramId,streak,onRestartTutorial,totalXP=0,C}) {  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const isPremium = true; // TODO: remplacer par isPremium réel quand RevenueCat sera branché
  const BtnC=mkBtn(C);
  const [form,setForm]=useState(profile||{name:"",age:"",weight:"",height:"",gender:null,level:null,goal:null,days:null,equipment:"salle",homeEquip:[],fitnessGoal:null});
  useEffect(()=>{if(profile)setForm(profile);},[profile]);
  // showThemes removed
  const [showFaq,setShowFaq]=useState(false);
  const [showInfoModal,setShowInfoModal]=useState(false);
  const [showCustomization,setShowCustomization]=useState(false);
  const [showProgramModal,setShowProgramModal]=useState(false);
  const [showMedalDetail,setShowMedalDetail]=useState(false);
  const set=(k,v)=>setForm(p=>({...p,[k]:v}));
  // curTheme removed

  // Infos résumées pour la carte identité
  const infoChips=[
    form.gender==="homme"?"Homme":form.gender==="femme"?"Femme":null,
    form.age?`${form.age} ans`:null,
    form.weight?`${form.weight} kg`:null,
    form.height?`${form.height} cm`:null,
  ].filter(Boolean);

  // Stats globales
  const totalSessions = sessions ? sessions.length : 0;
  const activeWeeklyTarget = (() => {
    if(!programs||!defaultProgramId) return profile?.days||null;
    // Cas custom : defaultProgramId est le groupName (string)
    const byGroup = programs.filter(p=>p.groupName===defaultProgramId);
    if(byGroup.length>0) return byGroup.length;
    // Cas généré : defaultProgramId est un id numérique
    const prog = programs.find(p=>p.id===defaultProgramId);
    if(!prog) return profile?.days||null;
    if(prog.days) return prog.days;
    const grp = prog.groupName||'';
    const match = grp.match(/(\d+)j$/);
    if(match) return parseInt(match[1]);
    if(grp) return programs.filter(p=>p.groupName===grp).length||1;
    return 1;
  })();
  const totalPRs = prs ? Object.values(prs).reduce((acc, pr) => acc + 1 + (pr.history||[]).length, 0) : 0;
  const totalHours = (() => {
    if(!sessions||sessions.length===0) return 0;
    const total = sessions.reduce((acc,s)=>{
      if(s.duration) return acc + (parseInt(s.duration)||0);
      return acc + 45;
    },0);
    return Math.round(total/60);
  })();

  return(
    <div style={{position:"fixed",inset:0,background:C.bg,overscrollBehavior:"none",display:"flex",flexDirection:"column",boxSizing:"border-box"}}>
      {/* Header */}
      <div style={{padding:"calc(env(safe-area-inset-top) + 20px) 20px 22px",flexShrink:0}}>
        <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 5px"}}>MON PROFIL</p>
        <h1 style={{color:C.text,fontSize:26,fontWeight:900,margin:0}}>Paramètres</h1>
      </div>

      {/* Corps flex */}
      <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"space-between",padding:"0 20px",minHeight:0}}>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>

          {/* ══ CARTE IDENTITÉ ══ */}
          {(()=>{
            const now = new Date();
            const weekStart = new Date(now);
            weekStart.setDate(now.getDate() - now.getDay() + (now.getDay()===0?-6:1));
            weekStart.setHours(0,0,0,0);
            const weekDays = new Set((sessions||[]).filter(s=>s.date&&s.type!=="tabata"&&new Date(s.date)>=weekStart).map(s=>s.date.slice(0,10))).size;
            const trainingDays = countTrainingDays(sessions);
            // Système XP/Niveaux
            const currentLevel = getLevelFromXP(totalXP);
            const { xpInLevel, xpNeeded, pct: xpPct, isMaxLevel } = getLevelProgress(totalXP);
            const medalCard = getMedalByLevel(currentLevel);
            const nextMedalCard = getNextMedalByLevel(currentLevel);
            // Compat ancienne détection médaille (pour newMedalKey)
            const levelsUntilNextMedal = nextMedalCard ? nextMedalCard.level - currentLevel : 0;
            return (
              <div style={{background:C.card,borderRadius:18,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                {/* Identité */}
                <button onClick={()=>setShowInfoModal(true)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"11px 14px",background:"none",border:"none",cursor:"pointer",textAlign:"left",boxSizing:"border-box"}}>
                  <div style={{width:48,height:48,borderRadius:"50%",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <span style={{color:"white",fontWeight:900,fontSize:22,lineHeight:1,userSelect:"none"}}>
                      {form.name ? form.name.trim()[0].toUpperCase() : "?"}
                    </span>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{color:C.text,fontWeight:800,fontSize:16,margin:"0 0 2px"}}>{form.name||"Votre prénom"}</p>
                    {form.fitnessGoal&&(
                      <span style={{background:`${C.accent}20`,color:C.accent,fontSize:11,fontWeight:800,borderRadius:8,padding:"2px 9px",border:`1px solid ${C.accent}40`,display:"inline-block",marginBottom:2}}>
                        {form.fitnessGoal==="force"?"Force":form.fitnessGoal==="hypertrophie"?"Hypertrophie":"Endurance"}
                      </span>
                    )}
                    {infoChips.length>0
                      ?<p style={{color:C.muted,fontSize:12,margin:"0 0 2px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{infoChips.join(" · ")}</p>
                      :<p style={{color:C.accent,fontSize:12,margin:"0 0 2px"}}>Compléter le profil →</p>
                    }
                    {(()=>{
                      const allSess=[...(sessions||[])].filter(s=>s.date).sort((a,b)=>a.date.localeCompare(b.date));
                      if(!allSess.length) return null;
                      const days=Math.floor((new Date()-new Date(allSess[0].date))/86400000);
                      return <p style={{color:C.muted,fontSize:11,margin:0}}>Membre depuis {days} jour{days!==1?"s":""}</p>;
                    })()}
                  </div>
                  <span style={{color:C.muted,fontSize:18,flexShrink:0}}>›</span>
                </button>
                {/* Stats depuis le début */}
                <div style={{height:1,background:C.border,margin:"0 14px"}}/>
                <div style={{padding:"8px 14px 4px"}}>
                  <p style={{color:C.muted,fontSize:9,letterSpacing:2,textAlign:"center",margin:"0 0 6px",textTransform:"uppercase"}}>Depuis le début</p>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr"}}>
                    {[
                      {val:totalSessions,label:"Séances"},
                      {val:totalPRs,label:"Records"},
                      {val:`${totalHours}h`,label:"Entraînement"},
                    ].map((s,i)=>(
                      <div key={i} style={{textAlign:"center",padding:"2px 0"}}>
                        <p style={{color:C.text,fontWeight:900,fontSize:20,margin:"0 0 1px",lineHeight:1}}>{s.val}</p>
                        <p style={{color:C.muted,fontSize:10,margin:0}}>{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Stats cette semaine */}
                {(weekDays>0||streak>0)&&(
                  <>
                    <div style={{height:1,background:C.border,margin:"8px 14px 0"}}/>
                    <div style={{padding:"8px 14px 10px"}}>
                      <p style={{color:C.muted,fontSize:9,letterSpacing:2,textAlign:"center",margin:"0 0 8px",textTransform:"uppercase"}}>Cette semaine</p>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:4}}>
                        <div style={{textAlign:"center"}}>
                          <p style={{color:C.accent,fontWeight:700,fontSize:13,margin:"0 0 2px"}}>{weekDays}{(()=>{const w=prs?Object.values(prs).filter(pr=>[...(pr.history||[]),{weight:pr.weight,date:pr.date}].some(e=>e.date&&new Date(e.date)>=weekStart)).length:0;return w>0?<span style={{color:C.accent}}> · {w} PR</span>:null;})()}</p>
                          <p style={{color:C.muted,fontSize:10,margin:0}}>Séances</p>
                        </div>
                        <div style={{textAlign:"center",borderLeft:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`}}>
                          {activeWeeklyTarget
                            ? <><p style={{color:C.accent,fontWeight:700,fontSize:13,margin:"0 0 2px"}}>{activeWeeklyTarget} / sem.</p><p style={{color:C.muted,fontSize:10,margin:0}}>Objectif</p></>
                            : <><p style={{color:C.muted,fontSize:13,margin:"0 0 2px"}}>—</p><p style={{color:C.muted,fontSize:10,margin:0}}>Objectif</p></>
                          }
                        </div>
                        <div style={{textAlign:"center"}}>
                          {streak>0
                            ? <><p style={{color:C.accent,fontWeight:700,fontSize:13,margin:"0 0 2px"}}>{streak} sem.</p><p style={{color:C.muted,fontSize:10,margin:0}}>Streak</p></>
                            : <><p style={{color:C.muted,fontSize:13,margin:"0 0 2px"}}>—</p><p style={{color:C.muted,fontSize:10,margin:0}}>Streak</p></>
                          }
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {/* Niveau + XP + Médaille */}
                <div style={{height:1,background:C.border,margin:"0 14px"}}/>
                <button onClick={()=>setShowMedalDetail(true)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"none",border:"none",cursor:"pointer",textAlign:"left",boxSizing:"border-box"}}>
                  {medalCard
                    ? <img src={medalCard.img} alt={medalCard.label} style={{width:48,height:48,objectFit:"contain",flexShrink:0}}/>
                    : <div style={{width:48,height:48,borderRadius:14,background:`${C.accent}18`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>
                      </div>
                  }
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:2}}>
                      <p style={{color:C.text,fontWeight:900,fontSize:16,margin:0}}>Niveau {currentLevel}</p>
                      {medalCard && <p style={{color:C.accent,fontSize:12,margin:0,fontWeight:700}}>{medalCard.label}</p>}
                    </div>
                    {!isMaxLevel ? (
                      <>
                        {nextMedalCard && <p style={{color:C.muted,fontSize:11,margin:"0 0 4px"}}>Prochain palier : {nextMedalCard.label} (niv. {nextMedalCard.level})</p>}
                        <div style={{height:4,background:C.border,borderRadius:4,overflow:"hidden",marginBottom:2}}>
                          <div style={{height:"100%",width:`${xpPct*100}%`,background:`linear-gradient(90deg,${C.accent},${C.accent2})`,borderRadius:4,transition:"width 0.6s ease"}}/>
                        </div>
                        <p style={{color:C.muted,fontSize:10,margin:0}}>{xpInLevel} / {xpNeeded} XP</p>
                      </>
                    ) : (
                      <p style={{color:C.accent,fontSize:12,margin:"2px 0 0",fontWeight:700}}>Niveau maximum — Calestan Collector 🏆</p>
                    )}
                  </div>
                  <span style={{color:C.muted,fontSize:18,flexShrink:0}}>›</span>
                </button>
              </div>
            );
          })()}

          {/* ══ CARTE ACTIONS ══ */}
          <div style={{background:C.card,borderRadius:18,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            {/* Personnalisation */}
            <button onClick={()=>setShowCustomization(true)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"11px 14px",background:"none",border:"none",cursor:"pointer",textAlign:"left",boxSizing:"border-box"}}>
              <div style={{width:48,height:48,borderRadius:14,background:`${C.accent}18`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:C.accent}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{color:C.text,fontWeight:800,fontSize:16,margin:"0 0 3px"}}>Personnalisation</p>
                <p style={{color:C.muted,fontSize:12,margin:0}}>Mode, thème, guidage vocal</p>
              </div>
              <span style={{color:C.muted,fontSize:18,flexShrink:0}}>›</span>
            </button>
            <div style={{height:1,background:C.border,margin:"0 14px"}}/>
            {/* Synchronisation */}
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px"}}>
              <div style={{width:48,height:48,borderRadius:14,background:`${C.accent}15`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{color:C.text,fontWeight:800,fontSize:16,margin:"0 0 3px"}}>{user ? (user.displayName||"Compte Google") : "Synchronisation"}</p>
                <p style={{color:C.muted,fontSize:12,margin:0}}>{user ? "Connecté · données synchronisées" : "Sauvegarder sur le cloud"}</p>
              </div>
              {user
                ? <button onClick={()=>setShowSignOutConfirm(true)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"4px 10px",color:C.muted,fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>Déco.</button>
                : <button onClick={onLogin} style={{background:"white",border:"1px solid #e0e0e0",borderRadius:8,padding:"5px 10px",display:"flex",alignItems:"center",gap:5,cursor:"pointer",boxShadow:"0 1px 3px rgba(0,0,0,0.1)",flexShrink:0}}>
                    <svg width="12" height="12" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                    <span style={{color:"#3c4043",fontWeight:700,fontSize:11}}>Connexion</span>
                  </button>
              }
            </div>
            <div style={{height:1,background:C.border,margin:"0 14px"}}/>
            {/* Support */}
            <button onClick={()=>setShowFaq(true)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"11px 14px",background:"none",border:"none",cursor:"pointer",textAlign:"left",boxSizing:"border-box"}}>
              <div style={{width:48,height:48,borderRadius:14,background:`${C.accent}15`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{color:C.text,fontWeight:800,fontSize:16,margin:"0 0 3px"}}>Support</p>
                <p style={{color:C.muted,fontSize:12,margin:0}}>Aide, FAQ & contact</p>
              </div>
              <span style={{color:C.muted,fontSize:18,flexShrink:0}}>›</span>
            </button>
          </div>
        </div>

        <p style={{color:C.muted,fontSize:11,textAlign:"center",margin:0,letterSpacing:0.5,paddingBottom:"calc(env(safe-area-inset-bottom) + 72px)"}}>Calestan v1.0</p>
      </div>

            {/* Modal infos personnelles */}
      {showInfoModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",backdropFilter:"blur(16px)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:300}}>
          <div style={{background:C.card,borderRadius:"24px 24px 0 0",padding:"28px 24px calc(env(safe-area-inset-bottom) + 90px)",width:"100%",maxWidth:480,border:`1px solid ${C.border}`,maxHeight:"calc(90vh - 83px)",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
              <p style={{color:C.text,fontWeight:900,fontSize:16,margin:0}}>Informations personnelles</p>
              <button onClick={()=>setShowInfoModal(false)} style={{background:"none",border:"none",color:C.muted,fontSize:28,cursor:"pointer",lineHeight:1}}>×</button>
            </div>

            {/* Prénom */}
            <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>PRÉNOM / PSEUDO</label>
            <input value={form.name||""} onChange={e=>set("name",e.target.value)} placeholder="ex: Thomas"
              style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:16,padding:"12px 14px",outline:"none",boxSizing:"border-box",marginBottom:16}}/>

            {/* Sexe */}
            <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>SEXE</label>
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              {[{id:"homme",label:"Homme"},{id:"femme",label:"Femme"}].map(s=>(
                <button key={s.id} onClick={()=>set("gender",s.id)}
                  style={{flex:1,padding:"10px 6px",borderRadius:11,border:`1px solid ${form.gender===s.id?C.accent:C.border}`,background:form.gender===s.id?`${C.accent}15`:C.faint,color:form.gender===s.id?C.accent:C.muted,fontWeight:700,fontSize:13,cursor:"pointer",transition:"all 0.15s"}}>
                  {s.label}
                </button>
              ))}
            </div>

            {/* Âge */}
            <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>ÂGE</label>
            <input type="number" value={form.age||""} onChange={e=>set("age",e.target.value)} placeholder="ex: 25"
              style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:16,padding:"12px 14px",outline:"none",boxSizing:"border-box",marginBottom:16}}/>

            {/* Poids + Taille */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:24}}>
              {[{l:"POIDS (kg)",k:"weight",p:"75"},{l:"TAILLE (cm)",k:"height",p:"178"}].map(f=>(
                <div key={f.k}>
                  <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>{f.l}</label>
                  <input type="number" value={form[f.k]||""} onChange={e=>set(f.k,e.target.value)} placeholder={f.p}
                    style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:16,padding:"12px 13px",outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
            </div>

            {/* Objectif fitness */}
            <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>OBJECTIF FITNESS</label>
            <div style={{display:"flex",gap:8,marginBottom:24}}>
              {[{id:"force",label:"Force"},{id:"hypertrophie",label:"Hypertrophie"},{id:"endurance",label:"Endurance"}].map(o=>(
                <button key={o.id} onClick={()=>set("fitnessGoal",o.id)}
                  style={{flex:1,padding:"10px 4px",borderRadius:11,border:`1px solid ${form.fitnessGoal===o.id?C.accent:C.border}`,background:form.fitnessGoal===o.id?`${C.accent}15`:C.faint,color:form.fitnessGoal===o.id?C.accent:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",transition:"all 0.15s"}}>
                  {o.label}
                </button>
              ))}
            </div>

            <button onClick={()=>{onSave(form);setShowInfoModal(false);}}
              style={{width:"100%",padding:"14px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:14,color:"white",fontWeight:800,fontSize:15,cursor:"pointer"}}>
              Enregistrer
            </button>
          </div>
        </div>
      )}


      {/* Modal Personnalisation */}
      {showCustomization&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",backdropFilter:"blur(16px)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:300}}>
          <div style={{background:C.card,borderRadius:"24px 24px 0 0",padding:"28px 24px calc(env(safe-area-inset-bottom) + 90px)",width:"100%",maxWidth:480,border:`1px solid ${C.border}`,maxHeight:"calc(90vh - 83px)",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
              <p style={{color:C.text,fontWeight:900,fontSize:16,margin:0}}>Personnalisation</p>
              <button onClick={()=>setShowCustomization(false)} style={{background:"none",border:"none",color:C.muted,fontSize:28,cursor:"pointer",lineHeight:1}}>×</button>
            </div>

            {/* Mode sombre / clair */}
            <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:10}}>MODE D'AFFICHAGE</label>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:C.surface,borderRadius:13,padding:"13px 15px",border:`1px solid ${C.border}`,marginBottom:18}}>
              <div><p style={{color:C.text,fontWeight:700,margin:"0 0 2px"}}>Mode {darkMode?"sombre":"clair"}</p><p style={{color:C.muted,fontSize:12,margin:0}}>Sombre par défaut</p></div>
              <button onClick={onToggleDark} style={{width:50,height:26,borderRadius:13,border:"none",cursor:"pointer",position:"relative",background:darkMode?C.accent:C.faint,transition:"background 0.2s"}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:"white",position:"absolute",top:3,left:darkMode?27:3,transition:"left 0.2s"}}/>
              </button>
            </div>

            {/* Couleur thème */}
            <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:10}}>COULEUR DU THÈME</label>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:13,padding:"14px 16px",marginBottom:18}}>
              <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                {/* Bloc 2x2 gratuit — taille fixe 44px */}
                <div style={{display:"grid",gridTemplateColumns:"44px 44px",gap:8,flexShrink:0}}>
                  {THEMES.filter(t=>!t.premium).map(t=>(
                    <button key={t.id} onClick={()=>onSetTheme(t.id)} title={t.name}
                      style={{width:44,height:44,borderRadius:10,border:`2px solid ${themeId===t.id?"white":"transparent"}`,cursor:"pointer",background:`linear-gradient(135deg,${t.a},${t.b})`,boxShadow:themeId===t.id?`0 0 0 2px ${t.a}`:undefined,position:"relative"}}>
                      {themeId===t.id&&<span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:13,fontWeight:900}}>✓</span>}
                    </button>
                  ))}
                </div>

                {/* Séparateur */}
                <div style={{width:1,background:C.border,alignSelf:"stretch",flexShrink:0}}/>

                {/* Bloc premium — même taille 44px par case */}
                <div style={{position:"relative"}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,44px)",gap:8}}>
                    {THEMES.filter(t=>t.premium).map(t=>(
                      isPremium ? (
                        <button key={t.id} onClick={()=>onSetTheme(t.id)} title={t.name}
                          style={{width:44,height:44,borderRadius:10,border:`2px solid ${themeId===t.id?"white":"transparent"}`,cursor:"pointer",background:`linear-gradient(135deg,${t.a},${t.b})`,boxShadow:themeId===t.id?`0 0 0 2px ${t.a}`:undefined,position:"relative"}}>
                          {themeId===t.id&&<span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:13,fontWeight:900}}>✓</span>}
                        </button>
                      ) : (
                        <div key={t.id} style={{width:44,height:44,borderRadius:10,background:`linear-gradient(135deg,${t.a},${t.b})`}}/>
                      )
                    ))}
                  </div>
                  {/* Overlay unique si non-premium */}
                  {!isPremium&&(
                    <div style={{position:"absolute",inset:0,borderRadius:12,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(2px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6}}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      <span style={{color:"white",fontSize:10,fontWeight:700,letterSpacing:1.5}}>PREMIUM</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Guidage vocal */}
            <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:10}}>OPTIONS AUDIO</label>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:C.surface,borderRadius:13,padding:"13px 15px",border:`1px solid ${C.border}`,marginBottom:24}}>
              <div><p style={{color:C.text,fontWeight:700,margin:"0 0 2px"}}>Guidage vocal</p><p style={{color:C.muted,fontSize:12,margin:0}}>Annonce les séries et le repos</p></div>
              <button onClick={onToggleVoice} style={{width:50,height:26,borderRadius:13,border:"none",cursor:"pointer",position:"relative",background:voiceEnabled?C.accent:C.faint,transition:"background 0.2s"}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:"white",position:"absolute",top:3,left:voiceEnabled?27:3,transition:"left 0.2s"}}/>
              </button>
            </div>

            <button onClick={()=>setShowCustomization(false)}
              style={{width:"100%",padding:"14px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:14,color:"white",fontWeight:800,fontSize:15,cursor:"pointer"}}>
              Fermer
            </button>
          </div>
        </div>
      )}


        {/* Confirmation déconnexion */}
        {showSignOutConfirm&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 28px"}}>
            <div style={{background:C.card,borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:340,boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
              <div style={{textAlign:"center",marginBottom:20}}>
                <div style={{width:52,height:52,borderRadius:"50%",background:`${C.red}18`,border:`2px solid ${C.red}40`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                </div>
                <h3 style={{color:C.text,fontWeight:900,fontSize:18,margin:"0 0 8px"}}>Se déconnecter ?</h3>
                <p style={{color:C.muted,fontSize:13,margin:0,lineHeight:1.5}}>Tes données restent sauvegardées dans le cloud. Tu pourras te reconnecter à tout moment.</p>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <button onClick={()=>{setShowSignOutConfirm(false);onSignOut();}}
                  style={{width:"100%",padding:"13px",background:C.red,border:"none",borderRadius:12,color:"white",fontWeight:800,fontSize:15,cursor:"pointer"}}>
                  Oui, me déconnecter
                </button>
                <button onClick={()=>setShowSignOutConfirm(false)}
                  style={{width:"100%",padding:"13px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontWeight:700,fontSize:15,cursor:"pointer"}}>
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Support Modal */}
        {showFaq&&(
          <div style={{position:"fixed",inset:0,background:C.bg,zIndex:200,display:"flex",flexDirection:"column",boxSizing:"border-box"}}>
            {/* Header */}
            <div style={{padding:"calc(env(safe-area-inset-top) + 20px) 20px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <button onClick={()=>setShowFaq(false)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",lineHeight:1,padding:0}}>←</button>
                <h3 style={{color:C.text,fontWeight:900,fontSize:18,margin:0}}>Support</h3>
              </div>
            </div>
            {/* Contenu scrollable */}
            <div style={{flex:1,overflowY:"auto",padding:"20px 20px calc(env(safe-area-inset-bottom) + 100px)",boxSizing:"border-box"}}>

              {/* Nous contacter */}
              <p style={{color:C.muted,fontSize:11,letterSpacing:2,fontWeight:700,margin:"0 0 10px"}}>CONTACT</p>
              <div style={{background:C.card,borderRadius:16,border:`1px solid ${C.border}`,padding:"16px 18px",marginBottom:24}}>
                <p style={{color:C.text,fontWeight:800,fontSize:15,margin:"0 0 6px"}}>Une question ou une suggestion ?</p>
                <p style={{color:C.muted,fontSize:13,margin:"0 0 14px",lineHeight:1.5}}>Tu peux nous écrire directement par mail. On répond à tous les messages, que ce soit pour signaler un bug, proposer une amélioration ou juste donner ton avis.</p>
                <button onClick={()=>{const s=encodeURIComponent('Contact Calestan');const b=encodeURIComponent(`Bonjour,

[Ton message ici]

---
Version: v1`);window.location.href=`mailto:contact@calestan.fr?subject=${s}&body=${b}`;}}
                  style={{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,padding:"11px 20px",color:"white",fontWeight:800,fontSize:14,cursor:"pointer",width:"100%"}}>
                  Envoyer un mail
                </button>
              </div>

              {/* FAQ */}
              <p style={{color:C.muted,fontSize:11,letterSpacing:2,fontWeight:700,margin:"0 0 10px"}}>FOIRE AUX QUESTIONS</p>
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
              {[
                {q:"Comment Calestan crée-t-il mon programme ?", a:"Lors de la configuration initiale, tu indiques ton niveau, ta fréquence d'entraînement, ton objectif et ton équipement disponible. Calestan génère automatiquement un programme adapté. Tu peux le modifier à tout moment depuis ton profil."},
                {q:"Comment fonctionne la progression automatique ?", a:"Après chaque séance, Calestan analyse tes performances. Si tu valides tous tes sets au complet, le poids est légèrement augmenté à la prochaine séance. Si tu n'atteins pas les répétitions prévues, le poids reste stable."},
                {q:"À quoi sert l'onglet Suivi ?", a:"L'onglet Suivi regroupe tout ton historique : la progression de tes poids par exercice, tes records personnels (PR), tes mensurations corporelles, le calendrier de tes séances, et un outil de partage de résumés hebdomadaires et mensuels."},
                {q:"Qu'est-ce que le Tabata ?", a:"Le Tabata est un mode d'entraînement fractionné à haute intensité. Tu alternes des phases de travail intense (20 secondes par défaut) et de repos court (10 secondes), sur plusieurs rounds. Idéal en complément de la musculation pour le cardio."},
                {q:"Comment partager une séance ?", a:"À la fin de chaque séance, un bouton Partager génère automatiquement une image résumant tes performances (exercices, volumes, durée). Tu peux également partager des résumés hebdomadaires et mensuels depuis l'onglet Suivi → Partage."},
                {q:"Qu'est-ce que les médailles ?", a:"Les médailles récompensent ta régularité. Elles se débloquent automatiquement selon ton nombre de jours d'entraînement : Bronze à 20 jours, Argent à 50, Or à 100, puis Diamant et Calestan Collector pour les plus assidus."},
                {q:"Mes données sont-elles sauvegardées ?", a:"Tes données sont d'abord sauvegardées localement sur ton appareil. En te connectant avec ton compte Google depuis l'onglet Profil, tu actives la synchronisation cloud via Firebase — ce qui te permet de retrouver tes données sur plusieurs appareils."},
                {q:"Comment réinitialiser l'application ?", a:"Le bouton Supprimer les données en bas de l'onglet Profil efface toutes tes données locales. Cette action est irréversible. Si tu avais activé la synchronisation Google, tes données cloud restent intactes."},
              ].map((item,i,arr)=>(
                <div key={i} style={{background:C.card,borderRadius:14,border:`1px solid ${C.border}`,padding:"14px 16px",marginBottom:i<arr.length-1?2:0}}>
                  <p style={{color:C.text,fontWeight:800,fontSize:14,margin:"0 0 6px"}}>{item.q}</p>
                  <p style={{color:C.muted,fontSize:13,margin:0,lineHeight:1.6}}>{item.a}</p>
                </div>
              ))}
              </div>

              {/* Revoir le tutoriel */}
              {onRestartTutorial&&(
                <div style={{marginTop:24,display:"flex",justifyContent:"center"}}>
                  <button onClick={()=>{setShowFaq(false);setTimeout(onRestartTutorial,200);}} style={{background:"none",border:`1px solid ${C.accent}40`,borderRadius:10,padding:"7px 18px",cursor:"pointer",color:C.accent,fontWeight:700,fontSize:12}}>
                    Revoir le tutoriel
                  </button>
                </div>
              )}

              {/* Supprimer les données */}
              <div style={{marginTop:12,paddingBottom:8,display:"flex",justifyContent:"center"}}>
                <button onClick={()=>{setShowFaq(false);setTimeout(onReset,200);}} style={{background:"none",border:`1px solid ${C.red}40`,borderRadius:10,padding:"7px 18px",cursor:"pointer",color:C.red,fontWeight:700,fontSize:12}}>
                  Supprimer les données
                </button>
              </div>

            </div>
          </div>
        )}
        {/* Modal détail médailles */}
        {showMedalDetail&&(()=>{
          const currentLevelModal = getLevelFromXP(totalXP);
          const { xpInLevel: xpInLvl, xpNeeded: xpNeed, pct: xpP, isMaxLevel: isMax } = getLevelProgress(totalXP);
          const medalModal = getMedalByLevel(currentLevelModal);
          const nextMedalModal = getNextMedalByLevel(currentLevelModal);
          const levelsLeft = nextMedalModal ? nextMedalModal.level - currentLevelModal : 0;
          const earnedMedals = MEDALS_DEF_LOGIC.filter(m => currentLevelModal >= m.level);
          return (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",backdropFilter:"blur(16px)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:300}} onClick={()=>setShowMedalDetail(false)}>
              <div style={{background:C.card,borderRadius:"24px 24px 0 0",padding:"28px 24px calc(env(safe-area-inset-bottom) + 90px)",width:"100%",maxWidth:480,border:`1px solid ${C.border}`,maxHeight:"calc(90vh - 83px)",overflowY:"auto",boxSizing:"border-box"}} onClick={e=>e.stopPropagation()}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
                  <p style={{color:C.text,fontWeight:900,fontSize:16,margin:0}}>Mes médailles</p>
                  <button onClick={()=>setShowMedalDetail(false)} style={{background:"none",border:"none",color:C.muted,fontSize:28,cursor:"pointer",lineHeight:1}}>×</button>
                </div>
                {/* Niveau actuel */}
                <div style={{background:C.surface,borderRadius:14,border:`1px solid ${C.border}`,padding:"16px",marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:isMax?0:10}}>
                    {medalModal
                      ? <img src={medalModal.img} alt={medalModal.label} style={{width:56,height:56,objectFit:"contain",flexShrink:0}}/>
                      : <div style={{width:56,height:56,borderRadius:14,background:"#cd7f3218",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>
                        </div>
                    }
                    <div style={{flex:1}}>
                      <p style={{color:C.text,fontWeight:900,fontSize:18,margin:"0 0 2px"}}>Niveau {currentLevelModal}</p>
                      <p style={{color:C.accent,fontSize:13,fontWeight:700,margin:0}}>{medalModal ? medalModal.label : "Débutant"}</p>
                    </div>
                    <p style={{color:C.muted,fontSize:12,margin:0,textAlign:"right"}}>{totalXP} XP</p>
                  </div>
                  {!isMax && (
                    <>
                      <div style={{height:5,background:C.border,borderRadius:5,overflow:"hidden",marginBottom:4}}>
                        <div style={{height:"100%",width:`${xpP*100}%`,background:`linear-gradient(90deg,${C.accent},${C.accent2})`,borderRadius:5,transition:"width 0.6s ease"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between"}}>
                        <p style={{color:C.muted,fontSize:11,margin:0}}>{xpInLvl} / {xpNeed} XP</p>
                        <p style={{color:C.muted,fontSize:11,margin:0}}>Niv. {currentLevelModal+1}</p>
                      </div>
                    </>
                  )}
                </div>
                {/* Prochaine médaille */}
                {nextMedalModal && (
                  <div style={{background:`${C.accent}12`,borderRadius:12,border:`1px solid ${C.accent}30`,padding:"12px 14px",marginBottom:16}}>
                    <p style={{color:C.accent,fontWeight:700,fontSize:13,margin:"0 0 3px"}}>Prochain palier : {nextMedalModal.label}</p>
                    <p style={{color:C.muted,fontSize:12,margin:0}}>
                      Niveau {nextMedalModal.level} — encore {levelsLeft} niveau{levelsLeft>1?"x":""} à gagner
                    </p>
                  </div>
                )}
                {/* Paliers débloqués */}
                {earnedMedals.length > 0 && (
                  <>
                    <p style={{color:C.muted,fontSize:11,letterSpacing:2,fontWeight:700,margin:"8px 0 10px"}}>PALIERS DÉBLOQUÉS</p>
                    {[...earnedMedals].reverse().map((m,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:i<earnedMedals.length-1?`1px solid ${C.border}`:"none"}}>
                        <p style={{color:C.text,fontWeight:700,fontSize:13,margin:0,flex:1}}>{m.label}</p>
                        <p style={{color:C.muted,fontSize:11,margin:0}}>Niv. {m.level}</p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          );
        })()}

    </div>
  );
}

export { countTrainingDays, ProfilePage, THEMES };
